/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { ApolloError } from "@apollo/client";
import {
  useRemoveReportMutation,
  RemoveReportMutation,
} from "~/generated/graphql";
import { showError, showSuccess } from "~/services/toast";
import { removeFromApolloCache } from "~/tools";

type Props = {
  reportName: string;
};

export function useRemoveReport({ reportName }: Props) {
  const [id, setId] = useState("");
  const [removeReportMutation, { client, loading }] = useRemoveReportMutation({
    onCompleted: (response: RemoveReportMutation) => {
      if (response.removeReport) {
        removeFromApolloCache(client.cache, `Report:${id}`, "reports");
        showSuccess(`The report ${reportName} has been deleted.`);
      } else {
        showError("Failed to delete the report.");
      }
    },
    onError: (error: ApolloError) => {
      showError(`Failed to delete the report: ${error.message}`);
    },
  });

  function removeReport(reportId: string) {
    setId(reportId);
    removeReportMutation({
      variables: {
        id: reportId,
      },
    });
  }

  return [removeReport, loading] as const;
}
